import {
  useState,
  useCallback,
} from 'react';

import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from "@azure/msal-react";

/**
 * Custom hook to call a web API using bearer token obtained from Microsoft Authentication Library
 */
const useFetchWithMsal = () => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const { result, error: msalError } = useMsalAuthentication(InteractionType.Redirect, {
    account: instance.getActiveAccount(),
    redirectUri: window.location.pathname.replace(/\/+$/, ''),
    scopes: ["921da248-9c89-4ceb-9ffa-eb812ce7c834/.default"]
  });

  /**
   * Internal method to execute fetch request with common logic
   * @param {string} method: GET, POST, PUT, DELETE
   * @param {String} endpoint: The endpoint to call
   * @param {Object} data: The data to send to the endpoint, if any
   * @param {Function} responseParser: Function to parse the response (json, text, etc.)
   * @returns Parsed response
   */
  const executeRequest = async (method, endpoint, data = null, responseParser) => {
    if (msalError) {
      setError(msalError);
      return;
    }

    if (result) {
      try {
        const headers = new Headers();
        const bearer = `Bearer ${result.accessToken}`;
        headers.append("Authorization", bearer);

        if (data) headers.append('Content-Type', 'application/json');

        let options = {
          method: method,
          headers: headers,
          body: data ? JSON.stringify(data) : null,
        };

        setIsLoading(true);
        const fetchResponse = await fetch(endpoint, options);
        if(!fetchResponse.ok) {
          const err = new Error("HTTP failure response");
          err.status = fetchResponse.status;
          try { err.serverErrors = await fetchResponse.json(); } catch {}
          throw err;
        }

        const response = await responseParser(fetchResponse);
        setData(response);

        setIsLoading(false);
        return response;
      } catch (e) {
        setError(e);
        setIsLoading(false);
        throw e;
      }
    }
  };

  /**
   * Execute a fetch request with the given options
   * @param {string} method: GET, POST, PUT, DELETE
   * @param {String} endpoint: The endpoint to call
   * @param {Object} data: The data to send to the endpoint, if any
   * @returns JSON response
   */
  const execute = async (method, endpoint, data = null) => {
    return executeRequest(method, endpoint, data, (response) => response.json());
  };

  /**
   * Execute a fetch request and return HTML response
   * @param {string} method: GET, POST, PUT, DELETE
   * @param {String} endpoint: The endpoint to call
   * @param {Object} data: The data to send to the endpoint, if any
   * @returns HTML string response
   */
  const executeHtml = async (method, endpoint, data = null) => {
    return executeRequest(method, endpoint, data, (response) => response.text());
  };

  return {
    isLoading,
    error,
    data,
    execute: useCallback(execute, [result, msalError]), // to avoid infinite calls when inside a `useEffect`
    executeHtml: useCallback(executeHtml, [result, msalError])
  };
};

export default useFetchWithMsal;